import { datadogLogs } from "@datadog/browser-logs";
import type { ICustomerQuery } from "@repo/shopify/src/queries/customer-gql";
import qs from "query-string";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Loading from "@components/molecules/Loading";

import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";
import useSocialLogin from "@hooks/useSocialLogin";

import { KAKAO_REDIRECT_URL } from "@const/constants";
import { LoadingType } from "@const/enum";
import { URLS } from "@const/urls";

import { checkAccessAllowed, getServerTime } from "@utils/archive-sale";
import { NOTIFICATIONS } from "@utils/notifications";

export default function KakaoOauth() {
  const navigate = useNavigate();

  const query = qs.parse(location.search.replace("?", ""));

  const { openModal, openLoginModal, closeLoginModal, closeModal } = useLayout();
  const { userLogout } = useUser();

  const { t } = useTranslation();

  const redirectUrl = JSON.parse(sessionStorage.getItem(KAKAO_REDIRECT_URL) || JSON.stringify("/")) || URLS.HOME;

  useEffect(() => {
    if (!query.code) {
      navigate(URLS.HOME, { replace: true });
    }
  }, []);

  useEffect(() => {
    handleCode();
  }, [query.code]);

  /** 인가 코드로 카카오 토큰 발급 */
  const getToken = useCallback(async (code: string | (string | null)[]) => {
    try {
      const url = "https://kauth.kakao.com/oauth/token";
      const param = qs.stringify({
        grant_type: "authorization_code",
        client_id: import.meta.env.VITE_REACT_APP_KAKAO_CLIENT_ID,
        redirect_uri: window.location.origin + URLS.KAKAO_OAUTH,
        code,
      });

      const response = await fetch(url, {
        method: "POST",
        body: param,
        headers: { "Content-type": "application/x-www-form-urlencoded;charset=utf-8" },
      }).then((res) => res.json());

      return response.access_token;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      datadogLogs.logger.error("[KAKAO] Failed to get token", { message });
      throw new Error(`카카오 토큰 발급 오류 : ${message}`);
    }
  }, []);

  const onComplete = (accessToken: string, customer: ICustomerQuery["customer"]) => {
    // getServerTime().then((serverTime) => {
    //   const isAllowed = checkAccessAllowed({ customer, currentTime: serverTime });
    //   if (!isAllowed) {
    //     userLogout();
    //     navigate(URLS.HOME);
    //
    //     openModal({
    //       title: "TUNE’s Archive Sale",
    //       description:
    //         "본 페이지는 TUNE 멤버십 Black / Platinum / Gold / Silver 회원을 대상으로 사전 진행되며, 이외 등급은 10월 30일 13시부터 접속 가능합니다.",
    //       confirmButton: {
    //         label: "Okay",
    //         onClick: () => {
    //           userLogout();
    //           // navigate(URLS.HOME);
    //           closeModal();
    //         },
    //       },
    //     });
    //
    //     return;
    //   }

    if (!customer.phone || !customer.birth?.value) {
      openLoginModal({ redirectUrl });
    } else {
      closeLoginModal();
      navigate(URLS.HOME);
    }

    navigate(redirectUrl, { replace: true });
    sessionStorage.removeItem(KAKAO_REDIRECT_URL);
    // });
  };

  const { isProgressing, socialLogin } = useSocialLogin({
    onComplete,
    onError: () => {
      navigate(URLS.HOME, { replace: true });
    },
  });

  const handleCode = async () => {
    if (query.code) {
      try {
        const token = await getToken(query.code);
        if (token) {
          await socialLogin(`=kakao&kakao_token=${token}`);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);

        openModal({
          title: "Login Error",
          description: t("toast.account.error.register_limit"),
          confirmButton: {
            label: "Okay",
            onClick: () => {
              closeModal();
            },
          },
        });

        navigate(URLS.HOME, { replace: true });
        datadogLogs.logger.error("[KAKAO] Failed to Login", { message });
        throw new Error(`[KAKAO] Failed to Login : ${message}`);
      }
    }
  };

  return isProgressing && <Loading description={t("kakaoOauth.isProgressing.description")} type={LoadingType.Screen} />;
}
